<template>
  <card-component class="community-list-card" :subject-key="subjectKey" @on-click="handleOpen">
    <template #header>
      <div class="card-header-thumbnail">
        <img v-if="showImage" class="community-image" alt="community-image" :src="groupImageUrl" />
        <CommunityIcon v-else class="icon" />
      </div>
      <div class="card-header-content">
        <h6 class="title">{{ name }}</h6>
        <ul class="metadata">
          <li>
            <span>{{ metadata }}</span>
            <span class="user-count">
              <svg-icon name="User16" kind="indicative" />
              <span>{{ teacherCount }}</span>
            </span>
          </li>
          <li>
            <span class="subject">{{ subjectTitle }}</span>
          </li>
        </ul>
        <div class="community-watermark"></div>
      </div>
    </template>
    <template #content>
      <label-component
        v-if="communityStatus"
        class="not-joined"
        kind="community-status"
        :text="t('community_group_list_community_not_joined_info_label')"
      />
    </template>
  </card-component>
</template>

<script setup lang="ts">
import CommunityIcon from "@images/space/Community.svg?component";
import { isEmpty } from "@utils/var.check";
import { formatSubjectDisplayName } from "@utils/subject.utils.ts";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

// Composables
const { t } = useI18n();
const router = useRouter();

// Props
interface Props {
  community: Record<string, unknown>; // replace with actual type if known
  subjectKey?: string;
  fromBrowseCommunity?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  subjectKey: "",
  fromBrowseCommunity: false,
});

// Computed
const name = computed(() => {
  return props.community.groupName;
});

const communityStatus = computed(() => {
  if (!props.fromBrowseCommunity) {
    return false;
  }

  return !props.community.joined;
});

const metadata = computed(() => {
  return t("metadata_community_label");
});

const subjectTitle = computed(() => {
  return isEmpty(props.subjectKey)
    ? null
    : formatSubjectDisplayName(props.community.subjectName, (value) => value.toLowerCase());
});

const teacherCount = computed(() => {
  return props.community.teacherCount;
});

const groupImageUrl = computed(() => {
  return props.community.imageUrl;
});

const showImage = computed(() => {
  return groupImageUrl.value != null;
});

// Methods
const handleOpen = function () {
  const routeInfo = {
    name: "StudentGroup",
    params: { collectionUuid: props.community.studentGroupCollectionUuid },
  };

  if (!props.fromBrowseCommunity) {
    router.push(routeInfo);
    return;
  }

  const route = router.resolve(routeInfo);
  window.open(route.href, "_blank");
};
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

$card-height: 160px;
$header-height: 100px;

div.community-list-card {
  // add specificity to override card-component properties
  &.card-component {
    flex-flow: column nowrap;
    height: $card-height;
    padding: $spacing-03;
    &._has-subject-code {
      &::before {
        // override CardComponent subject code border position
        top: 0;
      }
    }

    .card-header {
      display: flex;
      flex-wrap: nowrap;
      height: $header-height;
      padding: 2px;
      background-image: $community-card-gradient;
      background-clip: content-box, border-box;
      border-radius: 10px;

      .card-header-thumbnail {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 auto;
        width: 80px;
        height: 80px;
        margin: $spacing-03;
        .icon {
          transform: scale(1.3);
        }
        img {
          width: 100%;
          height: 100%;
          border-radius: 10px;
          object-fit: cover;
        }
      }

      .card-header-content {
        padding: $spacing-03;
        .title {
          @include carbon--type-style("card-title-02");
          @include line-clamp(2);
          margin-bottom: $spacing-01;
        }

        .metadata {
          padding-right: $spacing-06;
          .sls-icon {
            margin-right: $spacing-02;
            [fill] {
              fill: #6d7b8c;
              &[fill="#DFE8F9"] {
                opacity: 0.15;
              }
            }
          }

          li {
            flex: 0 0 100%;
            &:first-of-type {
              margin-bottom: $spacing-01;
            }

            // override styling from CardComponent
            &:not(:last-of-type) {
              padding: 0;
              &::after {
                display: none;
              }
            }

            > span {
              @include carbon--type-style("card-short-01");
              position: relative;
              text-transform: capitalize;
              &.user-count {
                display: flex;
                align-items: center;
              }
              &.subject {
                @include line-clamp(1);
                line-height: 1rem; // override line-height in line-clamp
              }

              // create dot divider
              &:not(:first-child) {
                margin-left: $spacing-03;
                &::before {
                  @include position-mixin(
                    $height: 2px,
                    $width: 2px,
                    $top: 50%,
                    $left: -4px,
                    $pseudo: true
                  );
                  transform: translateY(-50%);
                  border-radius: 50%;
                  background-color: $text-02;
                }
              }
            }
          }
        }

        .community-watermark {
          @include position-mixin($right: 0.5rem, $bottom: 2.5rem);
          height: 46px;
          width: 43px;
          background: url("/src/assets/images/space/CommunityWatermark.svg") no-repeat;
        }
      }
    }

    .card-content {
      display: flex;
      justify-content: flex-end;
      margin-top: $spacing-04;

      .label-component {
        margin: 0;
        span {
          @include carbon--type-style("card-label-01");
        }
      }
    }
  }
}
</style>
